import React, { FC, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import PropTypes from 'prop-types';

import { useEventListener } from '../../hooks';
import { useResizeObserver } from '../../hooks/useResizeObserver';
import sx from '../../internal/styles/sx';
import { Box } from '../../layouts';
import { CollapsibleProps } from './types';

const StyledBox = styled(Box, {
  shouldForwardProp,
})<CollapsibleProps>(
  css({
    transition: 'height 0.15s ease',
  }),
  sx
);

const Collapsible: FC<CollapsibleProps> = ({
  open = false,
  children,
  sx,
  ...rest
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxHeight, setBoxHeight] = useState<number>(0);
  const {
    contentRect: { height },
  } = useResizeObserver(boxRef);
  const [transitionEnd, setTransitionEnd] = useState<boolean>(false);
  const handleOverflow = () => {
    setTransitionEnd(true);
  };
  useEventListener('transitionend', handleOverflow);

  useEffect(() => {
    setBoxHeight(open ? height : 0);
    if (!open) {
      setTransitionEnd(false);
    }
  }, [open, height]);

  return (
    <StyledBox
      height={`${boxHeight}px`}
      overflow={open && transitionEnd ? 'visible' : 'hidden'}
      sx={sx}
    >
      <Box ref={boxRef} height={'auto'}>
        <Box aria-hidden={!open} {...rest} height={'auto'}>
          {children}
        </Box>
      </Box>
    </StyledBox>
  );
};

Collapsible.propTypes = {
  /**
   * open: boolean to open/close it
   */
  open: PropTypes.bool,
  sx: PropTypes.object,
};

export default Collapsible;
